/**
 * Activation Command Center — operator-friendly redesign (option A + B + C).
 * ────────────────────────────────────────────────────────────────────
 * Admin-only single-pane operational readiness dashboard.
 *
 * Consumes ONLY `/api/platform/readiness` (read-only aggregator).
 * There are NO mutation buttons on this page. The flag flips and rollout-state
 * advancements are still performed via the existing `/api/org/settings`
 * endpoint or the staircase scripts — this page is the **pre-flight check**.
 *
 * Permission required: `allocation.live_split_billing.view`
 *
 * UX additions in this rev (frontend-only):
 *  • Compact 4-pill health strip + auto-refresh countdown
 *  • "What to do next" callout pulled from `next_step`
 *  • 3 collapsible tabs (Rollout & Governance · Safety & Kill-Switches · Operational Telemetry)
 *  • Per-card status border + failure-count summary
 *  • "Show only failures" toggle
 *  • Plain-English titles, semantic Enabled/Disabled badges, tooltips
 *  • Sticky quick-jump nav (in-tab anchors)
 *  • All existing data-testids preserved.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import api from '@/lib/api';
import { SplitBillingDriftTile } from '@/components/admin/SplitBillingDriftTile';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Shield,
  ShieldAlert,
  Activity,
  RefreshCcw,
  Lock,
  Database,
  FileCheck2,
  Layers,
  ListChecks,
  ChevronRight,
  Sparkles,
  Info,
} from 'lucide-react';

const REFRESH_INTERVAL_MS = 30000;

// ─── Verdict palette ───────────────────────────────────────────────
const verdictPalette = {
  GREEN: {
    bg: 'bg-emerald-50', text: 'text-emerald-900', ring: 'ring-emerald-200', border: 'border-emerald-300',
    icon: <CheckCircle2 className="h-6 w-6 text-emerald-600" />,
    label: 'GREEN — Safe to proceed',
    chip: 'bg-emerald-600 text-white',
  },
  YELLOW: {
    bg: 'bg-amber-50', text: 'text-amber-900', ring: 'ring-amber-300', border: 'border-amber-300',
    icon: <AlertTriangle className="h-6 w-6 text-amber-600" />,
    label: 'YELLOW — Caution / controlled rollout',
    chip: 'bg-amber-600 text-white',
  },
  RED: {
    bg: 'bg-red-50', text: 'text-red-900', ring: 'ring-red-300', border: 'border-red-300',
    icon: <XCircle className="h-6 w-6 text-red-600" />,
    label: 'RED — Activation blocked',
    chip: 'bg-red-600 text-white',
  },
  UNKNOWN: {
    bg: 'bg-slate-50', text: 'text-slate-900', ring: 'ring-slate-200', border: 'border-slate-300',
    icon: <Activity className="h-6 w-6 text-slate-600" />,
    label: 'UNKNOWN — endpoint unreachable',
    chip: 'bg-slate-600 text-white',
  },
};

// ─── Helpers ───────────────────────────────────────────────────────
function semanticLabel(value, mode = 'enabled') {
  // mode: 'enabled' (true=Enabled), 'armed' (true=Armed), 'count' (numeric)
  if (value === null || value === undefined || value === '—') return '—';
  if (typeof value === 'boolean') {
    if (mode === 'armed') return value ? 'Armed' : 'Disarmed';
    if (mode === 'open')   return value ? 'Open'  : 'Closed';
    return value ? 'Enabled' : 'Disabled';
  }
  return String(value);
}

function relativeTime(date) {
  if (!date) return '—';
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

// ─── Pill (with optional tooltip) ──────────────────────────────────
function Pill({ label, value, ok, mode = 'enabled', tooltip }) {
  const isOk = typeof ok === 'boolean' ? ok : value === true || value === 'OK';
  const display = semanticLabel(value, mode);
  const testId = `pill-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const row = (
    <div
      className={`flex items-center justify-between rounded-md border px-3 py-1.5 text-sm transition-colors ${isOk ? 'border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50' : 'border-slate-200 bg-slate-50/60 hover:bg-slate-50'}`}
      data-testid={testId}
    >
      <span className="flex items-center gap-1.5 text-slate-700">
        {label}
        {tooltip && <Info className="h-3 w-3 text-slate-400" aria-hidden />}
      </span>
      <Badge variant={isOk ? 'default' : 'secondary'} className={`${isOk ? 'bg-emerald-600' : 'bg-slate-500'} font-medium`}>
        {display}
      </Badge>
    </div>
  );
  if (!tooltip) return row;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{row}</TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs text-xs">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

// ─── Section card with status border + collapsible all-green summary ───
function Section({ id, title, subtitle, icon, accent = 'slate', failureCount = 0, totalCount = 0, hideIfOk = false, children }) {
  const allOk = failureCount === 0 && totalCount > 0;
  // If "show only failures" hides this card, render nothing.
  if (hideIfOk && allOk) return null;

  const borderClass = failureCount > 0
    ? 'border-amber-300 ring-1 ring-amber-200'
    : allOk
      ? 'border-emerald-200'
      : `border-${accent}-200`;

  return (
    <Card id={id} className={`relative scroll-mt-24 ${borderClass}`} data-testid={`section-${id}`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-start justify-between gap-2 text-sm font-semibold text-slate-800">
          <div className="flex items-center gap-2">
            {icon}
            <div>
              <div>{title}</div>
              {subtitle && <div className="text-[10px] font-normal uppercase tracking-wider text-slate-400 mt-0.5">{subtitle}</div>}
            </div>
          </div>
          {totalCount > 0 && (
            <Badge variant="outline" className={`text-[10px] font-medium ${failureCount > 0 ? 'border-amber-400 text-amber-700' : 'border-emerald-300 text-emerald-700'}`}>
              {failureCount > 0 ? `${failureCount} issue${failureCount > 1 ? 's' : ''}` : 'All clear'}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5 pt-0">{children}</CardContent>
    </Card>
  );
}

// ─── Quick-jump sticky nav (in-tab) ─────────────────────────────────
function QuickJumpNav({ items, activeId, onJump }) {
  return (
    <nav className="sticky top-3 hidden xl:block self-start w-44 shrink-0" aria-label="Quick jump">
      <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
        <div className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">On this page</div>
        <ul className="space-y-0.5">
          {items.map(it => (
            <li key={it.id}>
              <button
                onClick={() => onJump(it.id)}
                className={`w-full text-left flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors ${activeId === it.id ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid={`jump-${it.id}`}
              >
                <ChevronRight className="h-3 w-3 shrink-0 opacity-50" />
                <span className="truncate">{it.label}</span>
                {it.failures > 0 && (
                  <span className="ml-auto inline-block h-1.5 w-1.5 rounded-full bg-amber-500" aria-label={`${it.failures} issues`} />
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

// ─── Top health strip ───────────────────────────────────────────────
function HealthStripPill({ label, value, tone = 'slate', testId }) {
  const tones = {
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    red: 'bg-red-100 text-red-800 border-red-200',
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <div className={`flex flex-col rounded-lg border px-3 py-2 ${tones[tone]}`} data-testid={testId}>
      <span className="text-[10px] font-semibold uppercase tracking-wider opacity-75">{label}</span>
      <span className="text-sm font-bold leading-tight mt-0.5">{value}</span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export default function ActivationCommandCenterPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL_MS / 1000);
  const [showOnlyFailures, setShowOnlyFailures] = useState(false);
  const [activeJumpId, setActiveJumpId] = useState(null);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/platform/readiness');
      setData(res?.data?.data || res?.data || null);
      setLastLoadedAt(new Date());
      setCountdown(REFRESH_INTERVAL_MS / 1000);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load readiness');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh + visible countdown
  useEffect(() => {
    load();
    intervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          load();
          return REFRESH_INTERVAL_MS / 1000;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  // ─── Derived slices ────────────────────────────────────────────────
  const verdict = data?.verdict || 'UNKNOWN';
  const palette = verdictPalette[verdict] || verdictPalette.UNKNOWN;
  const flags = data?.flags || {};
  const rollout = data?.rollout_state || {};
  const journal = data?.journal_counts || {};
  const finance = data?.finance_collections || {};
  const audit = data?.audit_health || {};
  const gates = data?.gate_baselines || {};
  const killSwitch = data?.kill_switch || {};
  const next_step = data?.next_step || {};
  const phase3 = data?.phase3_status || {};
  const allowlist = data?.allowlist_posture || {};
  const governance = data?.governance_posture || {};
  const recovery = data?.recovery_posture || {};

  // ─── Per-section failure counts (drive borders + jump-nav dots) ────
  const counts = useMemo(() => {
    const recoveryItems = [
      (recovery.critical_count ?? 0) === 0,
      (recovery.warn_count ?? 0) === 0,
      (recovery.drift?.orphan_in_flight ?? 0) === 0,
      (recovery.drift?.compensation_failed ?? 0) === 0,
      (recovery.drift?.unknown_status ?? 0) === 0,
    ];
    const govItems = [
      governance.dual_control_satisfied === true,
    ];
    const allowlistItems = [
      allowlist.enabled === true || allowlist.enabled === false, // tri-state ok
      allowlist.mode === 'OFF' || !governance.stage_2_active,
    ];
    const killItems = [
      killSwitch.scaffold_guard_enabled === true,
      killSwitch.mutation_endpoints_active === false,
    ];
    const flagItems = Object.values(flags).map(v => v === false);
    const rolloutItems = [
      rollout.phase3_stage === 'OFF' || rollout.phase3_stage !== undefined,
      rollout.mode === 'OFF' || rollout.mode !== undefined,
    ];
    const journalItems = Object.values(journal).map(v => v === 0);
    const reconItems = [data?.reconciliation?.enforced === false];
    const auditItems = [
      (audit.audit_logs_last_24h ?? 0) > 0,
      (audit.bulk_sentinel_residue ?? 0) < 10,
    ];
    const gateItems = Object.values(gates).map(v => v >= 0 && v < 500);

    const fail = (arr) => arr.filter(b => !b).length;
    return {
      recovery: { fail: fail(recoveryItems), total: recoveryItems.length },
      governance: { fail: fail(govItems), total: govItems.length },
      allowlist: { fail: fail(allowlistItems), total: allowlistItems.length },
      killSwitch: { fail: fail(killItems), total: killItems.length },
      flags: { fail: fail(flagItems), total: flagItems.length },
      rollout: { fail: fail(rolloutItems), total: rolloutItems.length },
      journal: { fail: fail(journalItems), total: journalItems.length },
      reconciliation: { fail: fail(reconItems), total: reconItems.length },
      audit: { fail: fail(auditItems), total: auditItems.length },
      gates: { fail: fail(gateItems), total: gateItems.length },
    };
  }, [recovery, governance, allowlist, killSwitch, flags, rollout, journal, audit, gates, data]);

  // ─── Jump-nav definitions per tab ──────────────────────────────────
  const navByTab = {
    rollout: [
      { id: 'sec-governance', label: 'STAGE_2 Governance', failures: counts.governance.fail },
      { id: 'sec-allowlist', label: 'Allowlist Posture', failures: counts.allowlist.fail },
      { id: 'sec-recovery', label: 'Compensation Recovery', failures: counts.recovery.fail },
      { id: 'sec-rollout', label: 'Rollout State', failures: counts.rollout.fail },
      { id: 'sec-next', label: 'Recommended Next Step', failures: 0 },
    ],
    safety: [
      { id: 'sec-kill', label: 'Kill-Switch', failures: counts.killSwitch.fail },
      { id: 'sec-flags', label: 'Phase 3 Feature Flags', failures: counts.flags.fail },
      { id: 'sec-recon', label: 'Reconciliation', failures: counts.reconciliation.fail },
    ],
    telemetry: [
      { id: 'sec-audit', label: 'Audit Health (24h)', failures: counts.audit.fail },
      { id: 'sec-journal', label: 'Journal Lifecycle', failures: counts.journal.fail },
      { id: 'sec-finance', label: 'Finance Collections', failures: 0 },
      { id: 'sec-gates', label: 'CI Gate Baselines', failures: counts.gates.fail },
      { id: 'sec-drift', label: 'Split-billing Drift', failures: 0 },
    ],
  };

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveJumpId(id);
    }
  };

  // ─── Top strip values ──────────────────────────────────────────────
  const stripVerdictTone = verdict === 'GREEN' ? 'emerald' : verdict === 'YELLOW' ? 'amber' : verdict === 'RED' ? 'red' : 'slate';
  const riskTone = (phase3.risk_level || '').toUpperCase() === 'CRITICAL' ? 'red'
    : (phase3.risk_level || '').toUpperCase() === 'HIGH' ? 'amber'
    : 'emerald';
  const mutationsTone = data?.mutations_possible ? 'red' : 'emerald';

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-4 p-4 md:p-6" data-testid="activation-command-center">
      {/* ─── Header ─── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Activation Command Center</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl">
            Pre-flight readiness aggregator for Phase 3 controlled rollout. Visibility only — no mutation controls on this page.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500 text-right hidden sm:block" data-testid="last-refreshed-label">
            <div>Last refreshed: <span className="font-medium text-slate-700">{lastLoadedAt ? relativeTime(lastLoadedAt) : '—'}</span></div>
            <div>Next auto-refresh in <span className="font-mono font-medium text-slate-700">{countdown}s</span></div>
          </div>
          <Button onClick={() => { load(); }} disabled={loading} data-testid="refresh-readiness-btn" variant="default">
            <RefreshCcw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Refreshing…' : 'Refresh now'}
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-4 text-sm text-red-900" data-testid="readiness-error">
            Failed to load readiness: {error}
          </CardContent>
        </Card>
      )}

      {/* ─── Compact Health Strip ─── */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <HealthStripPill
          label="Overall Verdict"
          value={palette.label.replace(/^\w+ — /, '')}
          tone={stripVerdictTone}
          testId="strip-verdict"
        />
        <HealthStripPill
          label="Risk Level"
          value={phase3.risk_level || '—'}
          tone={riskTone}
          testId="strip-risk"
        />
        <HealthStripPill
          label="Mutations Possible"
          value={data?.mutations_possible ? 'YES — Live' : 'No — Gated'}
          tone={mutationsTone}
          testId="strip-mutations"
        />
        <HealthStripPill
          label="Phase 3 Status"
          value={phase3.status || '—'}
          tone="slate"
          testId="strip-phase3"
        />
      </div>

      {/* ─── Verdict banner (retained for context + blockers) ─── */}
      <Card className={`border ${palette.ring} ${palette.bg}`} data-testid="readiness-verdict-banner">
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-white p-3 shadow-sm">{palette.icon}</div>
            <div className="flex-1 min-w-0">
              <div className={`text-[10px] font-semibold uppercase tracking-wider ${palette.text} opacity-80`}>Overall Verdict</div>
              <div className={`text-xl font-bold ${palette.text}`} data-testid="readiness-verdict">{palette.label}</div>
              <div className="mt-1 text-xs text-slate-700">
                Generated {data?.generated_at ? new Date(data.generated_at).toLocaleString() : '—'}
              </div>
            </div>
            <Badge className={data?.mutations_possible ? 'bg-red-600 text-white' : 'bg-slate-600 text-white'} data-testid="mutations-possible-badge">
              {data?.mutations_possible ? 'YES — LIVE' : 'NO — gated'}
            </Badge>
          </div>

          {Array.isArray(data?.blockers) && data.blockers.length > 0 && (
            <div className="mt-4 rounded-md border border-red-300 bg-white p-3" data-testid="readiness-blockers">
              <div className="text-sm font-semibold text-red-800">Active blockers</div>
              <ul className="mt-1 list-disc pl-5 text-sm text-red-700">
                {data.blockers.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(data?.notes) && data.notes.length > 0 && (
            <div className="mt-3 rounded-md border border-amber-300 bg-white p-3" data-testid="readiness-notes">
              <div className="text-sm font-semibold text-amber-800">Notes</div>
              <ul className="mt-1 list-disc pl-5 text-sm text-amber-700">
                {data.notes.map((n) => <li key={n}>{n}</li>)}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── What to do next callout ─── */}
      {(next_step.label || next_step.action) && (
        <Card className="border-sky-200 bg-sky-50/60" data-testid="recommended-callout">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="rounded-full bg-white p-2 shadow-sm shrink-0">
              <Sparkles className="h-5 w-5 text-sky-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-sky-700">What to do next</div>
              <div className="text-sm font-semibold text-slate-900 mt-0.5">
                Step {next_step.step ?? '—'}: {next_step.label || 'No recommendation available'}
              </div>
              {next_step.action && (
                <div className="text-xs text-slate-700 mt-1.5 leading-relaxed">{next_step.action}</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── Filter toolbar ─── */}
      <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Info className="h-3.5 w-3.5" />
          Tip: hover any metric for an explanation. Use the side nav to jump.
        </div>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer select-none" data-testid="only-failures-toggle-label">
          Show only failures
          <Switch
            checked={showOnlyFailures}
            onCheckedChange={setShowOnlyFailures}
            data-testid="only-failures-toggle"
          />
        </label>
      </div>

      {/* ─── Tabs ─── */}
      <Tabs defaultValue="rollout" className="w-full">
        <TabsList className="grid w-full grid-cols-3" data-testid="acc-tabs">
          <TabsTrigger value="rollout" data-testid="tab-rollout">
            Rollout &amp; Governance
            {(counts.governance.fail + counts.allowlist.fail + counts.recovery.fail + counts.rollout.fail) > 0 && (
              <Badge className="ml-2 bg-amber-500 text-white text-[10px] h-4 px-1.5">{counts.governance.fail + counts.allowlist.fail + counts.recovery.fail + counts.rollout.fail}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="safety" data-testid="tab-safety">
            Safety &amp; Kill-Switches
            {(counts.killSwitch.fail + counts.flags.fail + counts.reconciliation.fail) > 0 && (
              <Badge className="ml-2 bg-amber-500 text-white text-[10px] h-4 px-1.5">{counts.killSwitch.fail + counts.flags.fail + counts.reconciliation.fail}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="telemetry" data-testid="tab-telemetry">
            Operational Telemetry
            {(counts.audit.fail + counts.journal.fail + counts.gates.fail) > 0 && (
              <Badge className="ml-2 bg-amber-500 text-white text-[10px] h-4 px-1.5">{counts.audit.fail + counts.journal.fail + counts.gates.fail}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ─── TAB 1: Rollout & Governance ─── */}
        <TabsContent value="rollout" className="mt-4">
          <div className="flex gap-4">
            <QuickJumpNav items={navByTab.rollout} activeId={activeJumpId} onJump={jumpTo} />
            <div className="flex-1 grid grid-cols-1 gap-3 md:grid-cols-2">
              <Section
                id="sec-governance"
                title="STAGE_2 Governance"
                subtitle="Day 14 — Step 3"
                icon={<ShieldAlert className="h-4 w-4 text-fuchsia-600" />}
                failureCount={counts.governance.fail}
                totalCount={counts.governance.total}
                hideIfOk={showOnlyFailures}
              >
                <div
                  className={`rounded-md border px-3 py-2 text-sm ${governance.stage_2_active ? 'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-900' : 'border-slate-200 bg-slate-50 text-slate-700'}`}
                  data-testid="governance-posture-summary"
                >
                  <div className="font-semibold">
                    {governance.stage_2_active
                      ? `STAGE_2 ACTIVE — ${governance.mode || 'OFF'}`
                      : 'STAGE_2 INACTIVE — rollout stage = OFF'}
                  </div>
                  <div className="mt-1 text-xs">
                    Trigger: {governance.trigger_armed ? 'Armed (live mutations possible)' : 'Disarmed (refusal preserved)'}
                  </div>
                </div>
                <Pill label="Stage 2 active" value={governance.stage_2_active} ok={governance.stage_2_active === true || governance.stage_2_active === false} mode="enabled" tooltip="Whether STAGE_2 is the current rollout stage." />
                <Pill label="Dual-control satisfied" value={governance.dual_control_satisfied} ok={governance.dual_control_satisfied === true} mode="enabled" tooltip="Two distinct people (approver + actor) signed off on STAGE_2." />
                <Pill label="Approver" value={governance.approver_id || '—'} ok tooltip="User who approved the STAGE_2 transition." />
                <Pill label="Activation actor" value={governance.activation_actor || '—'} ok tooltip="User who executed the STAGE_2 activation." />
                <Pill label="Stage transitions" value={governance.stage_history_count ?? 0} ok tooltip="Total stage transitions recorded for this tenant." />
                {governance.rollback_actor && (
                  <Pill label="Last rollback actor" value={governance.rollback_actor} ok tooltip="User who performed the most recent rollback." />
                )}
                {Array.isArray(governance.warnings) && governance.warnings.length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="governance-warnings">
                    <div className="font-semibold">Governance warnings</div>
                    <ul className="mt-1 list-disc pl-4">
                      {governance.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                {governance.last_stage_transition && (
                  <div className="rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-700" data-testid="last-stage-transition">
                    <div className="font-semibold">Last transition</div>
                    <div>{governance.last_stage_transition.action}: {governance.last_stage_transition.from_stage} → {governance.last_stage_transition.to_stage}</div>
                    <div className="text-slate-500">{governance.last_stage_transition.at ? new Date(governance.last_stage_transition.at).toLocaleString() : ''}</div>
                  </div>
                )}
              </Section>

              <Section
                id="sec-allowlist"
                title="Allowlist Posture"
                subtitle="Day 13 — Step 2"
                icon={<Layers className="h-4 w-4 text-sky-600" />}
                failureCount={counts.allowlist.fail}
                totalCount={counts.allowlist.total}
                hideIfOk={showOnlyFailures}
              >
                <div
                  className={`rounded-md border px-3 py-2 text-sm ${allowlist.enabled ? 'border-sky-300 bg-sky-50 text-sky-900' : 'border-slate-200 bg-slate-50 text-slate-700'}`}
                  data-testid="allowlist-posture-summary"
                >
                  <div className="font-semibold">{allowlist.summary || '—'}</div>
                  {allowlist.rollout_warning && (
                    <div className="mt-1 text-xs text-amber-800" data-testid="allowlist-rollout-warning">{allowlist.rollout_warning}</div>
                  )}
                </div>
                <Pill label="Allowlist flag" value={allowlist.enabled} ok={allowlist.enabled === true} mode="enabled" tooltip="Whether per-shift allowlist gating is on." />
                <Pill label="Rollout mode" value={allowlist.mode || 'OFF'} ok={allowlist.mode === 'OFF'} tooltip="Current rollout mode (OFF / PER_SHIFT_ALLOWLIST / FULL)." />
                <Pill label="Rollout stage" value={allowlist.rollout_stage || 'OFF'} ok={allowlist.rollout_stage === 'OFF'} tooltip="Active rollout stage." />
                <Pill label="Allowlisted shifts" value={allowlist.size ?? 0} ok={(allowlist.size ?? 0) === 0} tooltip="Shifts that have explicitly opted into live mutations." />
                <Pill label="Shift opt-in eligible" value={allowlist.eligible_for_optin} ok={allowlist.eligible_for_optin === false} mode="enabled" tooltip="Whether new shifts can opt into the allowlist." />
              </Section>

              <Section
                id="sec-recovery"
                title="Compensation Recovery"
                subtitle="Day 15 — Pre-Trigger"
                icon={<RefreshCcw className="h-4 w-4 text-rose-600" />}
                failureCount={counts.recovery.fail}
                totalCount={counts.recovery.total}
                hideIfOk={showOnlyFailures}
              >
                <div
                  className={`rounded-md border px-3 py-2 text-sm ${recovery.verdict === 'SAFE' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : recovery.verdict === 'AT_RISK' ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-slate-200 bg-slate-50 text-slate-700'}`}
                  data-testid="recovery-posture-summary"
                >
                  <div className="font-semibold">Compensation sweep dry-run verdict: {recovery.verdict || 'UNKNOWN'}</div>
                  <div className="mt-1 text-xs">{recovery.note || '—'}</div>
                </div>
                <Pill label="Plan entries scanned" value={recovery.plan_count ?? 0} ok={(recovery.plan_count ?? 0) >= 0} tooltip="Live-promotion journal entries the dry-run inspected." />
                <Pill label="Critical actions" value={recovery.critical_count ?? 0} ok={(recovery.critical_count ?? 0) === 0} tooltip="High-severity actions the sweep would take." />
                <Pill label="Warning actions" value={recovery.warn_count ?? 0} ok={(recovery.warn_count ?? 0) === 0} tooltip="Lower-severity actions queued by the sweep." />
                <Pill label="Orphan IN_FLIGHT" value={recovery.drift?.orphan_in_flight ?? 0} ok={(recovery.drift?.orphan_in_flight ?? 0) === 0} tooltip="Journal entries stuck in IN_FLIGHT with no parent allocation." />
                <Pill label="COMPENSATION_FAILED" value={recovery.drift?.compensation_failed ?? 0} ok={(recovery.drift?.compensation_failed ?? 0) === 0} tooltip="Compensation runs that errored out and need manual review." />
                <Pill label="Unknown status" value={recovery.drift?.unknown_status ?? 0} ok={(recovery.drift?.unknown_status ?? 0) === 0} tooltip="Entries with a status the engine doesn't recognise." />
                {Array.isArray(recovery.blockers) && recovery.blockers.length > 0 && (
                  <div className="rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-900" data-testid="recovery-blockers">
                    <div className="font-semibold">Recovery blockers</div>
                    <ul className="mt-1 list-disc pl-4">
                      {recovery.blockers.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  </div>
                )}
              </Section>

              <Section
                id="sec-rollout"
                title="Rollout State"
                subtitle="Tenant snapshot"
                icon={<Layers className="h-4 w-4 text-slate-600" />}
                failureCount={counts.rollout.fail}
                totalCount={counts.rollout.total}
                hideIfOk={showOnlyFailures}
              >
                <Pill label="Tenant" value={rollout.org_id || '—'} ok tooltip="Org/tenant id this readiness snapshot represents." />
                <Pill label="Stage" value={rollout.phase3_stage || 'OFF'} ok={rollout.phase3_stage === 'OFF'} tooltip="Phase 3 stage marker (OFF / STAGE_1 / STAGE_2)." />
                <Pill label="Mode" value={rollout.mode || 'OFF'} ok={rollout.mode === 'OFF'} tooltip="Activation mode (OFF / PER_SHIFT_ALLOWLIST / FULL)." />
                <Pill label="Allowlist size" value={rollout.allowlist_size ?? 0} ok={(rollout.allowlist_size ?? 0) === 0} tooltip="Shifts currently in the allowlist." />
              </Section>

              <Section
                id="sec-next"
                title="Recommended Next Step"
                subtitle="Pre-flight guidance"
                icon={<CheckCircle2 className="h-4 w-4 text-slate-600" />}
                totalCount={0}
                hideIfOk={showOnlyFailures}
              >
                <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="recommended-next-step">
                  <div className="text-sm font-semibold text-slate-900">Step {next_step.step ?? '—'}: {next_step.label || '—'}</div>
                  <div className="mt-1 text-xs text-slate-600">{next_step.action}</div>
                </div>
              </Section>
            </div>
          </div>
        </TabsContent>

        {/* ─── TAB 2: Safety & Kill-Switches ─── */}
        <TabsContent value="safety" className="mt-4">
          <div className="flex gap-4">
            <QuickJumpNav items={navByTab.safety} activeId={activeJumpId} onJump={jumpTo} />
            <div className="flex-1 grid grid-cols-1 gap-3 md:grid-cols-2">
              <Section
                id="sec-kill"
                title="Kill-Switch"
                subtitle="Refusal enforcement"
                icon={<Lock className="h-4 w-4 text-slate-600" />}
                failureCount={counts.killSwitch.fail}
                totalCount={counts.killSwitch.total}
                hideIfOk={showOnlyFailures}
              >
                <Pill label="Scaffold guard enabled" value={killSwitch.scaffold_guard_enabled} ok={killSwitch.scaffold_guard_enabled === true} mode="enabled" tooltip="ZERO_LIVE_MUTATION_SCAFFOLD must be True to keep refusal posture." />
                <Pill label="Mutation endpoints active" value={killSwitch.mutation_endpoints_active} ok={killSwitch.mutation_endpoints_active === false} mode="enabled" tooltip="Whether live mutation endpoints accept writes." />
                <div className="text-xs text-slate-500" data-testid="kill-switch-note">{killSwitch.note}</div>
              </Section>

              <Section
                id="sec-flags"
                title="Phase 3 Feature Flags"
                subtitle="Tenant feature configuration"
                icon={<Shield className="h-4 w-4 text-slate-600" />}
                failureCount={counts.flags.fail}
                totalCount={counts.flags.total}
                hideIfOk={showOnlyFailures}
              >
                {Object.entries(flags).map(([k, v]) => (
                  <Pill
                    key={k}
                    label={k.replace(/_/g, ' ').replace(/enabled$/, '').trim()}
                    value={v}
                    ok={v === false}
                    mode="enabled"
                    tooltip={`Flag key: ${k}`}
                  />
                ))}
              </Section>

              <Section
                id="sec-recon"
                title="Reconciliation"
                subtitle="Closeout enforcement"
                icon={<FileCheck2 className="h-4 w-4 text-slate-600" />}
                failureCount={counts.reconciliation.fail}
                totalCount={counts.reconciliation.total}
                hideIfOk={showOnlyFailures}
              >
                <Pill
                  label="Enforced"
                  value={data?.reconciliation?.enforced}
                  ok={data?.reconciliation?.enforced === false}
                  mode="enabled"
                  tooltip="Whether claim-line reconciliation is enforced at closeout."
                />
                <div className="text-xs text-slate-500">{data?.reconciliation?.note}</div>
              </Section>
            </div>
          </div>
        </TabsContent>

        {/* ─── TAB 3: Operational Telemetry ─── */}
        <TabsContent value="telemetry" className="mt-4">
          <div className="flex gap-4">
            <QuickJumpNav items={navByTab.telemetry} activeId={activeJumpId} onJump={jumpTo} />
            <div className="flex-1 grid grid-cols-1 gap-3 md:grid-cols-2">
              <Section
                id="sec-audit"
                title="Audit Health (last 24h)"
                subtitle="Log emission counters"
                icon={<Activity className="h-4 w-4 text-slate-600" />}
                failureCount={counts.audit.fail}
                totalCount={counts.audit.total}
                hideIfOk={showOnlyFailures}
              >
                <Pill label="audit_logs emitted" value={audit.audit_logs_last_24h ?? '—'} ok={(audit.audit_logs_last_24h ?? 0) > 0} tooltip="Audit events emitted in the last 24h. Zero typically indicates a silent failure." />
                <Pill label="refusal_log emitted" value={audit.refusal_log_last_24h ?? '—'} ok={(audit.refusal_log_last_24h ?? 0) >= 0} tooltip="Refusal-log writes — should remain >0 while mutations are gated." />
                <Pill label="Bulk sentinel residue" value={audit.bulk_sentinel_residue ?? '—'} ok={(audit.bulk_sentinel_residue ?? 0) < 10} tooltip="Bulk sentinel records lingering past their TTL." />
              </Section>

              <Section
                id="sec-journal"
                title="Journal Lifecycle"
                subtitle="In-flight state counts"
                icon={<ListChecks className="h-4 w-4 text-slate-600" />}
                failureCount={counts.journal.fail}
                totalCount={counts.journal.total}
                hideIfOk={showOnlyFailures}
              >
                {Object.entries(journal).map(([k, v]) => (
                  <Pill key={k} label={k.replace(/_/g, ' ')} value={v} ok={v === 0} tooltip={`live_promotion_journal entries in state: ${k}`} />
                ))}
              </Section>

              <Section
                id="sec-finance"
                title="Finance Collections"
                subtitle="Row counts (live)"
                icon={<Database className="h-4 w-4 text-slate-600" />}
                totalCount={0}
                hideIfOk={false}
              >
                {Object.entries(finance).map(([k, v]) => (
                  <Pill key={k} label={k.replace(/_/g, ' ')} value={v} ok tooltip={`Total rows in collection: ${k}`} />
                ))}
              </Section>

              <Section
                id="sec-gates"
                title="CI Gate Baselines"
                subtitle="Coverage thresholds"
                icon={<ShieldAlert className="h-4 w-4 text-slate-600" />}
                failureCount={counts.gates.fail}
                totalCount={counts.gates.total}
                hideIfOk={showOnlyFailures}
              >
                {Object.entries(gates).map(([k, v]) => (
                  <Pill key={k} label={k.replace(/_/g, ' ')} value={v} ok={v >= 0 && v < 500} tooltip={`Test/lint baseline: ${k}`} />
                ))}
              </Section>

              <div id="sec-drift" className="md:col-span-2">
                <SplitBillingDriftTile />
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="text-xs text-slate-500" data-testid="command-center-footer">
        Platform Readiness Aggregator · auto-refreshes every {REFRESH_INTERVAL_MS / 1000}s · read-only — no mutation actions.
      </div>
    </div>
    </TooltipProvider>
  );
}
